// Classificação e texto dos erros da API, num lugar só (painel e app do aluno). Usado pelo `useMutacao`
// (toast padrão), pelo `retry` das consultas e pelas telas de erro.
import { ehOnboardingPendente, ehTermosPendentes, ErroApi, MENSAGEM_LIMITE } from "./cliente";

export const MENSAGEM_ERRO_GENERICO = "Algo deu errado do nosso lado. Tente de novo em instantes.";
export const MENSAGEM_SEM_CONEXAO = "Sem conexão. Confira a internet e tente de novo.";
export const MENSAGEM_SERVIDOR_INDISPONIVEL = "O servidor não respondeu agora. Tente de novo em instantes.";

/** Proxy sem a API atrás (deploy, reinício): 502/503/504. */
const STATUS_INDISPONIVEL = new Set([502, 503, 504]);

/**
 * Falha de transporte que pode passar sozinha (rede caiu, API reiniciando atrás do proxy). Consultas (GET,
 * idempotentes) repetem nesses casos; mutações nunca repetem sozinhas.
 */
export function ehFalhaTransitoria(e: unknown): boolean {
  return e instanceof ErroApi && (e.codigo === "SEM_CONEXAO" || STATUS_INDISPONIVEL.has(e.status));
}

/**
 * Erro "de infraestrutura" (rede, 5xx, limite, resposta quebrada): a tela não tem o que dizer de específico,
 * então o aviso é sempre o mesmo, dado pelo `useMutacao`. Os demais (4xx de regra: 409, 422, 403...) são da tela.
 */
export function ehErroDeInfra(e: unknown): boolean {
  if (!(e instanceof ErroApi)) return true;
  return (
    e.codigo === "SEM_CONEXAO" ||
    e.codigo === "LIMITE_EXCEDIDO" ||
    e.codigo === "RESPOSTA_INVALIDA" ||
    e.status >= 500
  );
}

/**
 * A própria API já leva a outra tela (401 -> login; 403 de senha temporária -> troca de senha; 409 de termos
 * pendentes -> folha "Termos atualizados"; 409 de onboarding pendente -> /boas-vindas/1). Sem toast nem erro na tela.
 */
export function ehRedirecionamento(e: unknown): boolean {
  return (
    (e instanceof ErroApi && (e.codigo === "NAO_AUTENTICADO" || e.codigo === "TROCA_DE_SENHA_OBRIGATORIA")) ||
    ehTermosPendentes(e) ||
    ehOnboardingPendente(e)
  );
}

/** Texto legível de um erro da API (a mensagem do servidor já vem em pt-BR para 4xx). */
export function mensagemDeErro(e: unknown, padrao = MENSAGEM_ERRO_GENERICO): string {
  if (e instanceof ErroApi) {
    if (e.codigo === "SEM_CONEXAO") return MENSAGEM_SEM_CONEXAO;
    if (e.codigo === "LIMITE_EXCEDIDO") return e.message || MENSAGEM_LIMITE;
    if (STATUS_INDISPONIVEL.has(e.status)) return MENSAGEM_SERVIDOR_INDISPONIVEL;
    if (e.status >= 500 || e.codigo === "RESPOSTA_INVALIDA") return padrao;
    if (e.status === 404 && e.codigo === "NAO_ENCONTRADO" && /route|rota/i.test(e.message))
      return "Esta função ainda não está disponível no servidor.";
    return e.message || padrao;
  }
  return padrao;
}

/**
 * Mensagem de um campo do formulário vinda da API: `detalhes.<campo>` de VALIDACAO (400) ou CONFLITO (409,
 * ex.: "Este e-mail já está em uso por Marina (professor).").
 */
export function mensagemDoCampo(e: unknown, campo: string): string | undefined {
  if (!(e instanceof ErroApi) || (e.codigo !== "VALIDACAO" && e.codigo !== "CONFLITO")) return undefined;
  return e.campo(campo);
}

/**
 * A mutação deve soltar o toast de erro? (regra do cabeçalho; função pura para teste). `silencioso` já inclui o
 * `meta.erroNaTela`.
 */
export function deveAvisarErro(
  erro: unknown,
  o: { silencioso: boolean; telaMostraErro: boolean; chamadorTrata: boolean },
): boolean {
  if (o.silencioso || o.telaMostraErro || ehRedirecionamento(erro)) return false;
  if (erro instanceof DOMException && erro.name === "AbortError") return false;
  if (o.chamadorTrata && !ehErroDeInfra(erro)) return false;
  return true;
}
